import { useState, useRef, useEffect } from 'react';
import { updateWorkspaceName } from '../../../apiServices/groupApi';
import { renameItem } from '../../../apiServices/projectApi';
import { updateResourceName } from '../../../apiServices/resourceApi';
import { updateUserName } from '../../../apiServices/userApi';
import { getExtFromFileName } from '../../../util/UtilFunctions';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Spinner } from '../../ui/Spinner';
import { useNotification } from '@/hooks/useNotification';

interface RenameItem {
  id?: string | { S: string };
  title?: string | { S: string };
  name?: string | { S: string };
  itemtype?: string | { S: string };
  cognitoAttributes?: { UserAttributes?: Array<{ Value: string }> };
  [k: string]: unknown;
}

interface RenameModalProps {
  item?: RenameItem;
  workspace?: { id: string | { S: string }; title?: string | { S: string } };
  title?: string;
  placeholder?: string;
  closeModal: () => void;
  updateIsQuerying: (querying: boolean) => void;
  refreshList?: () => void;
  onRenamed?: (id: string, newName: string) => void;
  updateCurrentUserInfo?: () => void;
  getWorkspacePeopleList?: () => void;
}

function getStr(val: string | { S: string } | undefined): string {
  if (!val) return '';
  return typeof val === 'string' ? val : val.S;
}

export default function RenameModal(props: RenameModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { notify } = useNotification();
  const inputRef = useRef<HTMLInputElement>(null);

  const itemType = getStr(props.item?.itemtype);
  const isResource = itemType === 'resource';

  const getCurrentName = (): string => {
    if (props.workspace) return getStr(props.workspace.title);
    if (isResource) {
      const fullName = getStr(props.item?.name) || getStr(props.item?.title);
      const ext = getExtFromFileName(fullName) as string;
      return ext ? fullName.replace(`.${ext}`, '') : fullName;
    }
    if (itemType === 'firstname') return props.item?.cognitoAttributes?.UserAttributes?.[0]?.Value || '';
    if (itemType === 'lastname') return props.item?.cognitoAttributes?.UserAttributes?.[1]?.Value || '';
    return getStr(props.item?.title) || getStr(props.item?.name);
  };

  const [value, setValue] = useState(getCurrentName);

  useEffect(() => {
    inputRef.current?.select();
  }, []);

  const fileExt = isResource
    ? (getExtFromFileName(getStr(props.item?.name)) as string | null)
    : null;

  const rename = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (value.length === 0) { setError('Name cannot be empty'); return; }
    if (value === getCurrentName()) { props.closeModal(); return; }
    setError('');
    try {
      setLoading(true);
      props.updateIsQuerying(true);
      const itemId = getStr(props.item?.id as string | { S: string });

      if (props.workspace) {
        await updateWorkspaceName(getStr(props.workspace.id as string | { S: string }), value);
      } else if (isResource) {
        const newFullName = fileExt ? `${value}.${fileExt}` : value;
        await updateResourceName(itemId, newFullName);
      } else if (itemType === 'folder' || itemType === 'project') {
        await renameItem(itemId, value);
      } else if (itemType === 'lastname') {
        const firstname = props.item!.cognitoAttributes!.UserAttributes![0].Value;
        await updateUserName(firstname, value);
        props.updateCurrentUserInfo?.();
        props.getWorkspacePeopleList?.();
      } else if (itemType === 'firstname') {
        const lastname = props.item!.cognitoAttributes!.UserAttributes![1].Value;
        await updateUserName(value, lastname);
        props.updateCurrentUserInfo?.();
        props.getWorkspacePeopleList?.();
      }

      props.updateIsQuerying(false);
      notify({ type: 'success', message: 'Renamed successfully' });
      if (props.onRenamed) {
        const newTitle = isResource && fileExt ? `${value}.${fileExt}` : value;
        props.onRenamed(itemId, newTitle);
      }
      props.refreshList?.();
      props.closeModal();
    } catch {
      props.updateIsQuerying(false);
      notify({ type: 'error', message: 'Could not rename' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={rename} className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Current name: <span className="font-medium text-accent-foreground">{getCurrentName()}{fileExt ? `.${fileExt}` : ''}</span>
      </p>
      <div className="flex items-center gap-2">
        <Input
          ref={inputRef}
          placeholder={props.placeholder || 'New name'}
          value={value}
          onChange={(e) => { setValue(e.target.value); setError(''); }}
          autoFocus
        />
        {fileExt && <span className="shrink-0 text-sm text-muted-foreground">.{fileExt}</span>}
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={props.closeModal} disabled={loading}>Cancel</Button>
        <Button type="submit" disabled={loading || value.length === 0}>
          {loading && <Spinner size="sm" />}
          Rename
        </Button>
      </div>
    </form>
  );
}
